import { isZero, Zero } from '@studio/models/ptz';
import type { PTZ } from '@studio/models/ptz';
import { isNotNil } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { Pipeline } from './pipeline.js';

/** Pumps movement intents into the specified pipeline. */
export class Piston {
  /** Intended movement. */
  #intent: PTZ = Zero;

  /** `window.setInterval` identifier. */
  #intervalId: T_Maybe<number>;

  /** Processing pipeline */
  readonly #pipeline: Pipeline;

  /** Update Frequency */
  readonly #updateInterval: number;

  /**
   * Generates repeating movement intents.
   *
   * @param pipeline - Movement intent processing pipeline.
   * @param updateFrequency - Movement update frequency in Hz.
   */
  public constructor(pipeline: Pipeline, updateFrequency: number) {
    this.#pipeline = pipeline;
    this.#updateInterval = 1000 / updateFrequency;
  }

  /**
   * Indicates if the piston is pumping.
   *
   * @returns `true` if the piston is pumping, `false` otherwise.
   */
  public get isPumping(): boolean {
    return isNotNil(this.#intervalId);
  }

  /**
   * Inject a movement intent into the pipeline.
   *
   * @param intent - Movement intent.
   */
  public process(intent: PTZ): void {
    this.#intent = intent;

    this.#setPumping(!isZero(this.#intent));
  }

  /** Stop pumping. */
  public stop(): void {
    // Clear all motion
    this.#intent = Zero;

    this.#setPumping(false);
  }

  /**
   * Start/stop internal movement interval.
   *
   * @param value - `true` if we should be are pumping, `false` otherwise.
   */
  #setPumping(value: boolean): void {
    const { isPumping } = this;

    // Has something changed?
    if (isPumping !== value) {
      const tick = (): void => {
        this.#pipeline.process(this.#intent);
      };

      // Start or stop?
      if (value) {
        // Start pumping
        this.#intervalId = window.setInterval(tick, this.#updateInterval);
      } else {
        // Stop pumping
        window.clearInterval(this.#intervalId);
        this.#intervalId = undefined;
      }

      // Tick on the leading edge of interval
      tick();
    }
  }
}
